/**
 * The operator workflow as a pure state machine.
 *
 * `transition(state, event, config)` never touches the outside world: it returns the next state
 * plus a list of effects (sounds, PIN verification, robot commands) for the caller to execute.
 * Each screen declares exactly which events it accepts; anything else is ignored, so a stray tap
 * or a late async result can never push the robot into an unexpected state.
 */
import { DEFAULT_LAMP_CHANNELS, PIN_LENGTH, WARDS } from './catalog';
import { bedKey, formatLastCycle } from './format';
import { TIMING } from './timing';
import type {
  AbortReason,
  ActiveCycle,
  CatalogSnapshot,
  CycleJob,
  CycleRecord,
  Operator,
  PinKey,
  PinVerification,
  RecordProblem,
  RobotPosition,
  Screen,
  SoundCue,
  Ward,
} from './types';

export interface MachineConfig {
  /** Multipliers applied to real elapsed time; >1 compresses the phase for demos. */
  readonly timeScale: { readonly countdown: number; readonly cycle: number };
  readonly estopRequiresConfirmation: boolean;
  readonly countdownSeconds: number;
  readonly cycleSeconds: number;
}

export type PinStatus = 'idle' | 'verifying' | 'granted' | 'denied' | 'unavailable' | 'locked';

export interface MachineState {
  readonly screen: Screen;
  /** Seconds spent in the current timed phase: boot, welcome, connect, or a PIN feedback hold. */
  readonly phaseElapsed: number;
  readonly pin: string;
  readonly pinStatus: PinStatus;
  /** Seconds until the unit unlocks, while `pinStatus` is 'locked'. */
  readonly pinRetryAfter: number | null;
  readonly operator: Operator | null;
  /** The hospital's wards; replaced by the server's list once signed in. */
  readonly wards: readonly Ward[];
  /** Lamp outputs (bulbs) wired to this unit. */
  readonly lampChannels: readonly number[];
  /** Bulbs the operator chose for the next cycle; never empty. */
  readonly channels: readonly number[];
  readonly ward: Ward | null;
  readonly bed: string | null;
  readonly position: RobotPosition | null;
  readonly countdownRemaining: number;
  readonly cycleTotal: number;
  readonly cycleRemaining: number;
  /** Epoch ms at which emission started. */
  readonly cycleStartedAt: number | null;
  readonly estopPending: boolean;
  readonly abortReason: AbortReason | null;
  /** The lamp controller did not confirm a stop — the lamp may still be lit. */
  readonly stopUnconfirmed: boolean;
  /** A cycle the server says is already running on this unit, to pick up after sign-in. */
  readonly resume: ActiveCycle | null;
  readonly lastRecord: CycleRecord | null;
  /**
   * The cycle's time is up but its result is not confirmed yet (by the switch's own record, or the
   * server). Nothing is shown as clean, and no bed is marked sanitised, until it is.
   */
  readonly recordPending: boolean;
  /** The server could not confirm the last completed cycle; the room must not be treated as clean. */
  readonly recordProblem: RecordProblem | null;
  /** `bedKey()`s sanitised since the unit powered on. */
  readonly sanitisedBeds: readonly string[];
  readonly completedCycles: number;
}

export type MachineEvent =
  /** `dt` is real seconds since the previous tick; `now` is epoch ms. */
  | { readonly type: 'TICK'; readonly dt: number; readonly now: number }
  | { readonly type: 'PIN_KEY'; readonly key: PinKey }
  | { readonly type: 'PIN_VERIFIED'; readonly result: PinVerification }
  | { readonly type: 'SELECT_WARD'; readonly ward: Ward }
  | { readonly type: 'SELECT_BED'; readonly bed: string }
  | { readonly type: 'CONFIRM_BED' }
  | { readonly type: 'SELECT_POSITION'; readonly position: RobotPosition }
  | { readonly type: 'TOGGLE_BULB'; readonly channel: number }
  | { readonly type: 'BACK_TO_WARDS' }
  | { readonly type: 'BACK_TO_BEDS' }
  | { readonly type: 'START_CYCLE' }
  | { readonly type: 'CANCEL_COUNTDOWN' }
  | { readonly type: 'EMERGENCY_STOP' }
  | { readonly type: 'EMERGENCY_STOP_CANCEL' }
  | { readonly type: 'EMERGENCY_STOP_CONFIRM' }
  | { readonly type: 'OCCUPANCY_BREACH' }
  | { readonly type: 'DEVICE_FAULT' }
  | { readonly type: 'RESTART_CYCLE' }
  | { readonly type: 'LOG_AND_NEXT' }
  | { readonly type: 'STANDBY' }
  /** The operator hands the tablet over: back to the PIN pad, wherever no lamp is running. */
  | { readonly type: 'SIGN_OUT' }
  // Results from the backend:
  | { readonly type: 'CATALOG_LOADED'; readonly catalog: CatalogSnapshot; readonly now: number }
  /** The server's authoritative audit record for the cycle just completed. */
  | { readonly type: 'CYCLE_RECORDED'; readonly record: CycleRecord }
  | { readonly type: 'CYCLE_RECORD_FAILED'; readonly problem: RecordProblem }
  /** The server ended the running cycle (machine switched off outside Evesix, failsafe, power check). */
  | { readonly type: 'CYCLE_LOST'; readonly reason: AbortReason }
  | { readonly type: 'STOP_UNCONFIRMED' };

export type MachineEffect =
  | { readonly type: 'sound'; readonly cue: SoundCue }
  | { readonly type: 'verifyPin'; readonly pin: string }
  | { readonly type: 'loadCatalog' }
  | { readonly type: 'signOut' }
  | { readonly type: 'robot.startEmission'; readonly job: CycleJob }
  /** Re-attach to a cycle the server is already running (after a tablet reload). */
  | { readonly type: 'robot.resumeCycle'; readonly cycleId: string }
  | { readonly type: 'robot.completeCycle'; readonly record: CycleRecord }
  | {
      readonly type: 'robot.emergencyStop';
      readonly reason: AbortReason;
      readonly job: CycleJob | null;
    };

export interface Transition {
  readonly state: MachineState;
  readonly effects: readonly MachineEffect[];
}

export function createInitialState(config: MachineConfig): MachineState {
  return {
    screen: 'boot',
    phaseElapsed: 0,
    pin: '',
    pinStatus: 'idle',
    pinRetryAfter: null,
    operator: null,
    wards: WARDS,
    lampChannels: DEFAULT_LAMP_CHANNELS,
    channels: DEFAULT_LAMP_CHANNELS,
    ward: null,
    bed: null,
    position: null,
    countdownRemaining: config.countdownSeconds,
    cycleTotal: config.cycleSeconds,
    cycleRemaining: config.cycleSeconds,
    cycleStartedAt: null,
    estopPending: false,
    abortReason: null,
    stopUnconfirmed: false,
    resume: null,
    lastRecord: null,
    recordPending: false,
    recordProblem: null,
    sanitisedBeds: [],
    completedCycles: 0,
  };
}

export function transition(
  state: MachineState,
  event: MachineEvent,
  config: MachineConfig,
): Transition {
  return onBackendResult(state, event) ?? HANDLERS[state.screen](state, event, config);
}

/** Screens from which a cycle already running on the unit is picked straight back up. */
const RESUMABLE_FROM: ReadonlySet<Screen> = new Set(['ward', 'bed', 'position']);

/** Asynchronous results from the backend; mostly data updates, wherever the operator is. */
function onBackendResult(state: MachineState, event: MachineEvent): Transition | null {
  switch (event.type) {
    case 'CATALOG_LOADED': {
      const sanitisedBeds = [...new Set([...event.catalog.sanitisedBeds, ...state.sanitisedBeds])];
      const wired = event.catalog.lampChannels.length
        ? event.catalog.lampChannels
        : DEFAULT_LAMP_CHANNELS;
      const kept = state.channels.filter((c) => wired.includes(c));
      const next = {
        ...state,
        wards: event.catalog.wards,
        sanitisedBeds,
        lampChannels: wired,
        channels: kept.length ? kept : wired,
        resume: event.catalog.activeCycle,
      };
      // Already past the intro: rejoin the running cycle now. Otherwise the intro hands over to it.
      if (next.resume && RESUMABLE_FROM.has(state.screen)) return resumeCycle(next, event.now);
      return { state: next, effects: NO_EFFECTS };
    }
    case 'CYCLE_RECORDED': {
      const local = state.lastRecord;
      if (!local || local.wardCode !== event.record.wardCode || local.bed !== event.record.bed) {
        return unchanged(state);
      }
      const confirmed = { lastRecord: event.record, recordPending: false, recordProblem: null };
      // A service account's test run proves the robot works; it does not clean the room.
      if (state.operator && !state.operator.realCycles) return update(state, confirmed);
      const key = bedKey(local.wardCode, local.bed);
      const completedAt = event.record.completedAt;
      const lastCycle = formatLastCycle(completedAt, Date.parse(completedAt));
      const withLastCycle = (w: Ward): Ward =>
        w.code === local.wardCode ? { ...w, lastCycle } : w;
      return update(state, {
        ...confirmed,
        wards: state.wards.map(withLastCycle),
        ward: state.ward && withLastCycle(state.ward),
        sanitisedBeds: state.sanitisedBeds.includes(key)
          ? state.sanitisedBeds
          : [...state.sanitisedBeds, key],
      });
    }
    case 'CYCLE_RECORD_FAILED':
      if (state.screen !== 'report') return update(state, { recordPending: false });
      return update(state, { recordPending: false, recordProblem: event.problem }, sound('alarm'));
    case 'STOP_UNCONFIRMED':
      return update(state, { stopUnconfirmed: true }, sound('alarm'));
    default:
      return null;
  }
}

/**
 * Rejoins a cycle the server is running (the tablet was reloaded mid-cycle), with the time left
 * computed from when the machine was actually switched on.
 */
function resumeCycle(state: MachineState, now: number): Transition {
  const active = state.resume;
  if (!active) return unchanged(state);
  const protocolPerWallSecond = active.durationSeconds / active.wallClockSeconds;
  const elapsed = Math.max(0, (now - active.startedAt) / 1000) * protocolPerWallSecond;
  return update(
    state,
    {
      screen: 'cycle',
      phaseElapsed: 0,
      ward: active.ward,
      bed: active.bed,
      position: active.position,
      channels: active.channels,
      cycleTotal: active.durationSeconds,
      cycleRemaining: Math.max(0, active.durationSeconds - elapsed),
      cycleStartedAt: active.startedAt,
      estopPending: false,
      abortReason: null,
      resume: null,
    },
    { type: 'robot.resumeCycle', cycleId: active.cycleId },
    sound('advance'),
  );
}

/** The job the robot would run for the current selection, or null if the selection is incomplete. */
export function cycleJob(state: MachineState, config: MachineConfig): CycleJob | null {
  const { ward, bed, position, operator } = state;
  if (!ward || !bed || !position || !operator) return null;
  return {
    wardCode: ward.code,
    wardName: ward.name,
    bed,
    positionId: position.id,
    positionLabel: position.label,
    operatorName: operator.name,
    durationSeconds: state.cycleTotal,
    wallClockSeconds: state.cycleTotal / config.timeScale.cycle,
    channels: state.channels,
  };
}

// ── helpers ─────────────────────────────────────────────────────────────────

type Handler = (state: MachineState, event: MachineEvent, config: MachineConfig) => Transition;

const NO_EFFECTS: readonly MachineEffect[] = [];

function unchanged(state: MachineState): Transition {
  return { state, effects: NO_EFFECTS };
}

function update(
  state: MachineState,
  patch: Partial<MachineState>,
  ...effects: MachineEffect[]
): Transition {
  return { state: { ...state, ...patch }, effects };
}

function sound(cue: SoundCue): MachineEffect {
  return { type: 'sound', cue };
}

/** Accumulates `dt` into the current phase and calls `onDone` once `duration` has elapsed. */
function advancePhase(
  state: MachineState,
  dt: number,
  duration: number,
  onDone: () => Transition,
): Transition {
  const elapsed = state.phaseElapsed + dt;
  return elapsed >= duration ? onDone() : update(state, { phaseElapsed: elapsed });
}

function backToWards(state: MachineState): Transition {
  return update(state, { screen: 'ward', bed: null, position: null }, sound('back'));
}

function startCountdown(state: MachineState, config: MachineConfig): Transition {
  return update(
    state,
    {
      screen: 'countdown',
      countdownRemaining: config.countdownSeconds,
      cycleTotal: config.cycleSeconds,
      estopPending: false,
      abortReason: null,
    },
    sound('start'),
  );
}

function beginEmission(state: MachineState, config: MachineConfig, now: number): Transition {
  const job = cycleJob(state, config);
  if (!job) return backToWards(state);
  return update(
    state,
    {
      screen: 'cycle',
      countdownRemaining: 0,
      cycleRemaining: state.cycleTotal,
      cycleStartedAt: now,
      estopPending: false,
    },
    { type: 'robot.startEmission', job },
    sound('emissionStart'),
  );
}

function completeCycle(state: MachineState, config: MachineConfig, now: number): Transition {
  const job = cycleJob(state, config);
  if (!job) return backToWards(state);
  const key = bedKey(job.wardCode, job.bed);
  const completedCycles = state.completedCycles + 1;
  // The bed is marked sanitised only once the result is confirmed (CYCLE_RECORDED).
  const record: CycleRecord = {
    ...job,
    logId: `${key}-${String(completedCycles).padStart(3, '0')}`,
    startedAt: new Date(state.cycleStartedAt ?? now).toISOString(),
    completedAt: new Date(now).toISOString(),
  };
  return update(
    state,
    {
      screen: 'report',
      cycleRemaining: 0,
      completedCycles,
      lastRecord: record,
      recordPending: true,
    },
    { type: 'robot.completeCycle', record },
    sound('complete'),
  );
}

/** The stop command is always emitted first, before any UI feedback. */
function abort(state: MachineState, config: MachineConfig, reason: AbortReason): Transition {
  return update(
    state,
    { screen: 'aborted', abortReason: reason, estopPending: false },
    { type: 'robot.emergencyStop', reason, job: cycleJob(state, config) },
    sound('error'),
  );
}

/** Screens an operator may sign out from: none has a lamp running. */
export const SIGN_OUT_FROM: ReadonlySet<Screen> = new Set([
  'ward',
  'bed',
  'position',
  'aborted',
  'report',
]);

/** Whether the operator can sign out now. Never while a stop is unconfirmed: the lamp may be lit. */
export function canSignOut(state: MachineState): boolean {
  return state.operator !== null && SIGN_OUT_FROM.has(state.screen) && !state.stopUnconfirmed;
}

/** Back to the PIN pad for the next operator, forgetting this one's selection. */
function signOut(state: MachineState): Transition {
  if (!canSignOut(state)) return unchanged(state);
  return update(
    state,
    {
      screen: 'login',
      operator: null,
      ward: null,
      bed: null,
      position: null,
      pin: '',
      pinStatus: 'idle',
      pinRetryAfter: null,
      phaseElapsed: 0,
      estopPending: false,
      abortReason: null,
      lastRecord: null,
      recordPending: false,
      recordProblem: null,
      resume: null,
    },
    { type: 'signOut' },
    sound('back'),
  );
}

function onPinKey(state: MachineState, key: PinKey): Transition {
  if (state.pinStatus !== 'idle') return unchanged(state);
  if (key === 'CLR') return update(state, { pin: '' }, sound('clear'));
  if (key === 'DEL') return update(state, { pin: state.pin.slice(0, -1) }, sound('clear'));
  if (state.pin.length >= PIN_LENGTH) return unchanged(state);
  const pin = state.pin + key;
  if (pin.length < PIN_LENGTH) return update(state, { pin }, sound('tap'));
  return update(state, { pin, pinStatus: 'verifying' }, sound('tap'), { type: 'verifyPin', pin });
}

// ── screens ─────────────────────────────────────────────────────────────────

const HANDLERS: Readonly<Record<Screen, Handler>> = {
  boot(state, event) {
    if (event.type !== 'TICK') return unchanged(state);
    return advancePhase(state, event.dt, TIMING.boot, () =>
      update(state, { screen: 'login', phaseElapsed: 0 }, sound('advance')),
    );
  },

  login(state, event) {
    switch (event.type) {
      case 'PIN_KEY':
        return onPinKey(state, event.key);
      case 'PIN_VERIFIED': {
        if (state.pinStatus !== 'verifying') return unchanged(state);
        const { result } = event;
        if (result.ok) {
          return update(
            state,
            { pinStatus: 'granted', operator: result.operator, phaseElapsed: 0 },
            sound('granted'),
            { type: 'loadCatalog' },
          );
        }
        if (result.reason === 'locked') {
          return update(
            state,
            { pinStatus: 'locked', pinRetryAfter: result.retryAfterSeconds, phaseElapsed: 0 },
            sound('error'),
          );
        }
        return update(
          state,
          { pinStatus: result.reason === 'invalid' ? 'denied' : 'unavailable', phaseElapsed: 0 },
          sound('error'),
        );
      }
      case 'TICK': {
        const reset = () =>
          update(state, { pin: '', pinStatus: 'idle', pinRetryAfter: null, phaseElapsed: 0 });
        switch (state.pinStatus) {
          case 'granted':
            return advancePhase(state, event.dt, TIMING.accessGrantedHold, () =>
              update(state, { screen: 'welcome', pin: '', pinStatus: 'idle', phaseElapsed: 0 }),
            );
          case 'denied':
            return advancePhase(state, event.dt, TIMING.accessDeniedHold, reset);
          case 'unavailable':
          case 'locked':
            return advancePhase(state, event.dt, TIMING.accessUnavailableHold, reset);
          default:
            return unchanged(state);
        }
      }
      default:
        return unchanged(state);
    }
  },

  welcome(state, event) {
    if (event.type !== 'TICK') return unchanged(state);
    return advancePhase(state, event.dt, TIMING.welcome, () =>
      update(state, { screen: 'connect', phaseElapsed: 0 }, sound('advance')),
    );
  },

  connect(state, event) {
    if (event.type !== 'TICK') return unchanged(state);
    return advancePhase(state, event.dt, TIMING.connect, () =>
      state.resume
        ? resumeCycle(state, event.now)
        : update(state, { screen: 'ward', phaseElapsed: 0 }, sound('advance')),
    );
  },

  ward(state, event) {
    if (event.type === 'SIGN_OUT') return signOut(state);
    if (event.type !== 'SELECT_WARD') return unchanged(state);
    return update(
      state,
      { screen: 'bed', ward: event.ward, bed: null, position: null },
      sound('tap'),
    );
  },

  bed(state, event) {
    switch (event.type) {
      case 'SELECT_BED':
        return update(state, { bed: event.bed }, sound('tap'));
      case 'CONFIRM_BED':
        if (!state.bed) return unchanged(state);
        return update(state, { screen: 'position', position: null }, sound('advance'));
      case 'BACK_TO_WARDS':
        return backToWards(state);
      case 'SIGN_OUT':
        return signOut(state);
      default:
        return unchanged(state);
    }
  },

  position(state, event, config) {
    switch (event.type) {
      case 'SELECT_POSITION':
        return update(state, { position: event.position }, sound('tap'));
      case 'TOGGLE_BULB': {
        if (!state.lampChannels.includes(event.channel)) return unchanged(state);
        const on = state.channels.includes(event.channel);
        // At least one bulb must stay selected: a cycle with no light would disinfect nothing.
        if (on && state.channels.length === 1) return update(state, {}, sound('error'));
        const channels = on
          ? state.channels.filter((c) => c !== event.channel)
          : [...state.channels, event.channel].sort((a, b) => a - b);
        return update(state, { channels }, sound('tap'));
      }
      case 'BACK_TO_BEDS':
        return update(state, { screen: 'bed' }, sound('back'));
      case 'START_CYCLE':
        if (state.channels.length === 0 || !cycleJob(state, config)) return unchanged(state);
        return startCountdown(state, config);
      case 'SIGN_OUT':
        return signOut(state);
      default:
        return unchanged(state);
    }
  },

  countdown(state, event, config) {
    switch (event.type) {
      case 'TICK': {
        const remaining = state.countdownRemaining - event.dt * config.timeScale.countdown;
        if (remaining <= 0) return beginEmission(state, config, event.now);
        const second = Math.ceil(remaining);
        if (second === Math.ceil(state.countdownRemaining)) {
          return update(state, { countdownRemaining: remaining });
        }
        const cue = second <= TIMING.countdownFinalStretch ? 'countdownFinal' : 'countdownTick';
        return update(state, { countdownRemaining: remaining }, sound(cue));
      }
      case 'CANCEL_COUNTDOWN':
        return update(state, { screen: 'position' }, sound('cancel'));
      default:
        return unchanged(state);
    }
  },

  cycle(state, event, config) {
    switch (event.type) {
      case 'TICK': {
        // Keeps counting while a stop confirmation is open: the machine is still running, and
        // the screen must never drift from what the relay is actually doing.
        const remaining = state.cycleRemaining - event.dt * config.timeScale.cycle;
        if (remaining > 0) return update(state, { cycleRemaining: remaining });
        return completeCycle(state, config, event.now);
      }
      case 'EMERGENCY_STOP':
        if (!config.estopRequiresConfirmation) return abort(state, config, 'MANUAL_EMERGENCY_STOP');
        if (state.estopPending) return unchanged(state);
        return update(state, { estopPending: true }, sound('alarm'));
      case 'EMERGENCY_STOP_CANCEL':
        if (!state.estopPending) return unchanged(state);
        return update(state, { estopPending: false }, sound('resume'));
      case 'EMERGENCY_STOP_CONFIRM':
        if (!state.estopPending) return unchanged(state);
        return abort(state, config, 'MANUAL_EMERGENCY_STOP');
      case 'OCCUPANCY_BREACH':
        return abort(state, config, 'OCCUPANCY_BREACH');
      case 'DEVICE_FAULT':
        return abort(state, config, 'DEVICE_FAULT');
      case 'CYCLE_LOST':
        return abort(state, config, event.reason);
      default:
        return unchanged(state);
    }
  },

  aborted(state, event, config) {
    const cleared = { ...state, stopUnconfirmed: false };
    switch (event.type) {
      case 'RESTART_CYCLE':
        return startCountdown(cleared, config);
      case 'BACK_TO_WARDS':
        return backToWards(cleared);
      case 'SIGN_OUT':
        return signOut(state);
      default:
        return unchanged(state);
    }
  },

  report(state, event) {
    switch (event.type) {
      case 'LOG_AND_NEXT':
        return update(
          state,
          { screen: 'ward', bed: null, position: null, recordPending: false, recordProblem: null },
          sound('advance'),
        );
      case 'STANDBY':
      case 'SIGN_OUT':
        return signOut(state);
      default:
        return unchanged(state);
    }
  },
};
